import { useTranslation } from 'react-i18next'
import {
  Text,
  Box,
  Columns,
  Column,
  Stack,
  Alert,
  TextLink,
  IconArrowUpRight,
  IconBed,
  IconRuler,
  IconCalendarAlt,
  Inline,
  useResponsiveValue
} from '@machobear/design-system'
import { isEmpty, toNumber } from 'lodash'
import { format } from 'date-fns'
import ENV_VARS from '@/config'
import { getAreaMeasurementLabel } from '@/utils/listings'
import { getImageUrl } from '@/utils/image'
import useProfile from '@/hooks/use-profile'
import { useGetSuiteInfo } from '@/hooks/use-sponsored-listing'
import { PriceDisplay } from './price-display'
import InfoBox from '../../components/info-box'
import { FormBox, FormBoxSkeleton } from '../../components'
import { Listing } from '../../types'

const { WEB_URL } = ENV_VARS

const getAddress = (data: any) => {
  if (isEmpty(data)) return ''
  const { full_street_name, city, state } = data?.building
  const hideUnit = data.hide_unit_number === 1 && data.unit_type_txt_id !== 'condo'
  return `${!hideUnit && data.unit_number ? data.unit_number + ' - ' : ''} ${full_street_name}, ${city}, ${state}`
}

const getCoverImageUrl = (data: any) => {
  if (isEmpty(data)) return ''

  const coverPhotoImage = data?.images?.filter((image: any) => image.tag === 'cover_photo')
  const coverPhotoData = coverPhotoImage?.[0] ?? data?.images?.[0]

  if (isEmpty(coverPhotoData))
    return getImageUrl('https://placehold.co/400x300', { width: 400, height: 300, quality: null })

  return getImageUrl(coverPhotoData?.aws_s3_key, { width: 400, height: 300, quality: null })
}

const SummaryOverview = ({ data }: { data: Listing }) => {
  const { t } = useTranslation()
  const profile = useProfile()
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const suiteInfo = useGetSuiteInfo(data.listing_id)
  const address: string = getAddress(data)
  const coverImageUrl = getCoverImageUrl(data)
  const sizeUnit: string = getAreaMeasurementLabel(profile?.setting_size_unit ?? 'feet')
  const availabilityDate: string = data?.availability_date
    ? format(new Date(data.availability_date), 'MMM do, yyyy')
    : ''

  if (!data || !profile || suiteInfo.isLoading) {
    return (
      <FormBox>
        <FormBoxSkeleton />
      </FormBox>
    )
  }

  const typeText = {
    condo: t('common.apartment', 'Apartment'),
    house: t('common.house', 'House'),
    townhouse: t('common.townhouse', 'Townhouse')
  }

  const scopeText = {
    entire: t('Private space'),
    partial: t('Shared space'),
    single: t('common.room', 'Room')
  }

  const bedroomCount = toNumber(suiteInfo.data.bedrooms)
  const bathroomCount = toNumber(suiteInfo.data.bathrooms)
  const bedroomText =
    bedroomCount > 0
      ? t('b.listing.edit.count_beds.text', {
          count: bedroomCount,
          defaultValue: '{{count}} Bed',
          defaultValue_plural: '{{count}} Beds'
        })
      : t('common.studio', 'Studio')
  const bathroomText = t('b.listing.edit.count_baths.text', {
    count: bathroomCount,
    defaultValue: '{{count}} Bath',
    defaultValue_plural: '{{count}} Baths'
  })

  return (
    <Box data-testid="SummaryOverview">
      <FormBox>
        <Stack space="small">
          <Text size={isMobile ? 'xlarge' : 'large'} weight="strong" component="h3">
            {address}
          </Text>
          <Inline space="xxsmall">
            <Text size="small" tone="secondary">
              {typeText[data.unit_type_txt_id]}
            </Text>
            <Text size="small" tone="secondary">
              ·
            </Text>
            <Text size="small" tone="secondary">
              {scopeText[data.unit_type_scope_txt_id]}
            </Text>
            {data.gr_unit === '1' && (
              <>
                <Text size="small" tone="secondary">
                  ·
                </Text>
                <Text size="small" tone="secondary">
                  {t('Multi-unit listing')}
                </Text>
              </>
            )}
          </Inline>
        </Stack>
        <>
          {data?.verification_message_state === 'pending' && (
            <Alert tone="caution">
              <Text size="small">
                {t(
                  'To keep our community safe, we manually verify all new buildings which takes 1-2 business days. Your listing will be published upon approval.'
                )}{' '}
                <TextLink href="https://liv.rent/" target="_blank">
                  {t('common.learn_more', 'Learn more')}
                </TextLink>
              </Text>
            </Alert>
          )}
          <Columns space="xlarge" collapseBelow="tablet">
            <Column>
              <a
                href={`${WEB_URL}/rental-listings/${data?.listing_id}`}
                target="_blank"
                rel="noreferrer"
                aria-label={t('Preview listing in new tab')}
              >
                <Box
                  position="relative"
                  boxShadow="small"
                  overflow="hidden"
                  borderRadius="standard"
                  background="neutralLight"
                  width="full"
                  style={{
                    minHeight: '170px',
                    aspectRatio: isMobile ? '9/5' : '9/6',
                    backgroundImage: `url(${coverImageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <Box
                    background="neutralSoft"
                    borderRadius="circle"
                    margin="xsmall"
                    padding="xxsmall"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    position="absolute"
                    top={0}
                    right={0}
                  >
                    <IconArrowUpRight size="small" />
                  </Box>
                </Box>
              </a>
            </Column>
            <Column>
              <Box display="flex" alignItems="center" justifyContent="flexStart" height="full">
                <Stack space={{ mobile: 'gutter', tablet: 'large' }}>
                  <PriceDisplay listing={data} />
                  <Stack space="gutter">
                    <Text tone="secondary">
                      <Inline space="xsmall">
                        <IconBed />
                        <Box>
                          {bedroomText} · {bathroomText}
                        </Box>
                      </Inline>
                    </Text>
                    <Text tone="secondary">
                      <Inline space="xsmall">
                        <IconRuler />
                        <Box>
                          {data.gr_unit === '1'
                            ? `${data.gr_min_size} - ${data.gr_max_size}`
                            : data.size}{' '}
                          {sizeUnit}
                        </Box>
                      </Inline>
                    </Text>
                    <Text tone="secondary">
                      <Inline space="xsmall">
                        <IconCalendarAlt />
                        <Box>
                          {t('Available on {{availabilityDate}}', {
                            availabilityDate
                          })}
                        </Box>
                      </Inline>
                    </Text>
                  </Stack>
                </Stack>
              </Box>
            </Column>
          </Columns>
          {data.hide_unit_number === 1 && (
            <InfoBox>
              {t('Your unit number and street address will be hidden in the published listing.')}
            </InfoBox>
          )}
        </>
      </FormBox>
    </Box>
  )
}

export default SummaryOverview
